import React, { useState, useEffect } from "react";
import ReactTable from "react-table";
import { Button, UncontrolledTooltip } from "reactstrap";
import s from "../../styles.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExport,
  faPlusCircle,
  faCalendarTimes,
  faCalendarCheck
} from "@fortawesome/free-solid-svg-icons";
import permissionCodes from "../../../../../utils/permissionCodes";
import { userHasPermission } from "../../../../../utils/accessTree";
import DeportivaEventoAsignable from "../../../../../types/DeportivaEventoAsignable";
import { Archivo } from "../../../expedientsV2controls/types";
import ArchivoControl from "../../../expedientsV2controls/archivo/archivo";
import { programaReglamentoFormConfig } from "./BAsignacionProgramaReglamento.form";
import { programaCedulaFormConfig } from "./BAsignacionProgramaCedula.form";
import { Preview } from "../../../../../types/IAction";
import { formatIsoDate } from "../../../../helpers/formatters";

const {
  COMISIONES_V2_ASIGNAR_EVENTO: ASIGNAR_EVENTO,
  COMISIONES_V2_ASIGNAR_EVENTO_OMITIR_CADUCIDAD: OMITIR_CADUCIDAD
} = permissionCodes;

export interface TableProps {
  page: Page<DeportivaEventoAsignable>;
  loading: boolean;
  onFetchData: ({ page, pageSize }) => void;
  onExport: () => void;
  onAsignar: (row: DeportivaEventoAsignable) => void;
  onVerMembresia: (row: DeportivaEventoAsignable) => void;
  onRegenerar: (
    row: DeportivaEventoAsignable,
    archivo: Archivo,
    values: any
  ) => Promise<Preview<any>>;
  onCincelFirma: (
    row: DeportivaEventoAsignable,
    archivo: Archivo
  ) => Promise<Preview<any>>;
  onOmitirCaducidad: (row: DeportivaEventoAsignable) => void;
}

export const MemoTable = React.memo(
  ({
    page,
    loading,
    onFetchData,
    onExport,
    onAsignar,
    onVerMembresia,
    onRegenerar,
    onCincelFirma,
    onOmitirCaducidad
  }: TableProps) => {
    // State de las columnas de la tabla
    const [columns, setColumns] = useState(
      columnsFormatter(
        onAsignar,
        onVerMembresia,
        onRegenerar,
        onCincelFirma,
        onOmitirCaducidad
      )
    );
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);

    // Actualización de estilos
    useEffect(() => {
      setColumns(
        columnsFormatter(
          onAsignar,
          onVerMembresia,
          onRegenerar,
          onCincelFirma,
          onOmitirCaducidad
        )
      );
    }, []);

    // Actualización de datos de paginación
    useEffect(() => {
      if (page) {
        setPageNumber(page.number);
        setTotalPages(page.totalPages);
      }
    }, [page]);

    const mostrados = page && page.numberOfElements;
    const totales = page && page.totalElements;
    return (
      <>
        {/* Botón para exportar */}
        <div className="row">
          {!!page && !!page.content && !!page.content.length && (
            <>
              <div className="col-6 p-0" style={{ lineHeight: "40px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Invitados mostrados: {mostrados} de {totales}
                </span>
              </div>
              <div
                className="col-6 p-0 text-right"
                style={{ lineHeight: "40px" }}
              >
                <Button
                  hidden
                  color="primary"
                  onClick={onExport}
                  disabled={loading}
                  className="mr-2"
                >
                  <FontAwesomeIcon icon={faFileExport} />
                  {!loading ? " Exportar" : " Espere..."}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Tabla */}
        <div className="row mt-2">
          <div style={{ width: "100%" }}>
            <ReactTable
              manual
              data={(page && page.content) || []}
              columns={columns}
              loading={loading}
              defaultPageSize={100}
              className="-striped -highlight"
              onFetchData={onFetchData}
              page={pageNumber}
              pages={totalPages}
              showPagination={true}
              showPageSizeOptions={false}
              getTrProps={() => ({ style: { height: "43px" } })}
              sortable={false}
            />
          </div>
        </div>
      </>
    );
  }
);

function columnsFormatter(
  onAsignar: (row: DeportivaEventoAsignable) => void,
  onVerMembresia: (row: DeportivaEventoAsignable) => void,
  onRegenerar: (
    row: DeportivaEventoAsignable,
    archivo: Archivo,
    values: any
  ) => Promise<Preview<any>>,
  onCincelFirma: (
    row: DeportivaEventoAsignable,
    archivo: Archivo
  ) => Promise<Preview<any>>,
  onOmitirCaducidad: (row: DeportivaEventoAsignable) => void
) {
  return [
    {
      Header: "Factura",
      id: "folioFactura",
      width: 88,
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: simpleAccessor("folioFactura")
    },
    {
      Header: "Producto",
      id: "producto",
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: simpleAccessor("producto")
    },
    {
      Header: "Fecha de venta",
      id: "fechaVenta",
      width: 112,
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: dateAccessor("fechaVenta")
    },
    {
      Header: "Membresía",
      id: "membresia",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 160,
      accessor: (row: DeportivaEventoAsignable) => {
        return (
          <div className={`${fieldClassName(row)}`}>
            <a
              hidden={!row.idPublico}
              href="#"
              onClick={e => {
                e.preventDefault();
                onVerMembresia(row);
              }}
            >
              {row.clubMembresia} ({row.idPublico})
            </a>
            <span hidden={!!row.idPublico}>No aplica</span>
          </div>
        );
      }
    },
    {
      Header: "Id Cliente",
      id: "idPersona",
      width: 88,
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: simpleAccessor("idPersona")
    },
    {
      Header: "Cliente",
      id: "nombrePersona",
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: simpleAccessor("nombrePersona")
    },
    {
      Header: "Disponibles",
      id: "eventosDisponibles",
      width: 88,
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: simpleAccessor("eventosDisponibles")
    },
    {
      Header: "Caducidad",
      id: "fechaCaducidad",
      width: 100,
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: dateAccessor("fechaCaducidad")
    },
    {
      Header: "Cédula",
      id: "cedula",
      width: 168,
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: (row: DeportivaEventoAsignable) => {
        return documentAccessor(
          onRegenerar,
          onCincelFirma,
          row,
          row.cedulaProgramaDeportivo
        );
      }
    },
    {
      Header: "Reglamento",
      id: "reglamento",
      width: 168,
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: (row: DeportivaEventoAsignable) => {
        const archivo =
          row.reglamentoProgramaDeportivo || row.reglamentoClasePersonalizada;

        return documentAccessor(onRegenerar, onCincelFirma, row, archivo);
      }
    },
    {
      Header: "",
      id: "acciones",
      className: s.tdClear,
      width: 80,
      accessor: (row: DeportivaEventoAsignable) => {
        return (
          <div className={`${fieldClassName(row)} text-center`}>
            &nbsp;
            {renderBotonOmitirCaducidad(row, onOmitirCaducidad)}
            {renderBotonAsignar(row, onAsignar)}
            &nbsp;
          </div>
        );
      }
    }
  ];
}

function simpleAccessor(key: keyof DeportivaEventoAsignable) {
  return (row: DeportivaEventoAsignable) => {
    return <div className={fieldClassName(row)}>{row[key]}&nbsp;</div>;
  };
}

function dateAccessor(key: keyof DeportivaEventoAsignable) {
  return (row: DeportivaEventoAsignable) => {
    const fecha = row[key] ? formatIsoDate(row[key] as string) : "";
    return <div className={fieldClassName(row)}>{fecha}&nbsp;</div>;
  };
}

function fieldClassName(row: DeportivaEventoAsignable) {
  // Si la factura está cancelada, aplicar clase especial
  if (!row.isPagado) {
    return `${s.fieldAsignacion} ${s.facturaCancelada}`;
  }

  if (row.caducado && !row.ignorarCaducidad) {
    return `${s.fieldAsignacion} ${s.instanciaCaducada}`;
  }

  return row.asignable
    ? `${s.fieldAsignacion}`
    : `${s.fieldAsignacion} ${s.noAsignable}`;
}

function documentAccessor(
  onRegenerar: (
    row: DeportivaEventoAsignable,
    archivo: Archivo,
    values: any
  ) => Promise<Preview<any>>,
  onCincelFirma: (
    row: DeportivaEventoAsignable,
    archivo: Archivo
  ) => Promise<Preview<any>>,
  row: DeportivaEventoAsignable,
  archivo: Archivo
) {
  if (!archivo) {
    return <div className={`${fieldClassName(row)}`}>&nbsp;</div>;
  }

  let docForm = null;
  if (archivo.codigo === "CINCEL_CEDULA_PROGRAMA") {
    docForm = programaCedulaFormConfig;
  } else if (archivo.codigo === "CINCEL_PROGRAMA_REGLAMENTO") {
    docForm = programaReglamentoFormConfig;
  }

  const estiloFirmado = archivo.completo
    ? s.documentoFirmado
    : s.documentoNoFirmado;
  return (
    <div className={`${fieldClassName(row)} ${estiloFirmado}`}>
      <ArchivoControl
        key={"archivo-item-" + archivo.idExpediente + "-" + archivo.idItem}
        archivo={archivo}
        userId={0}
        sm={true}
        botonCorreo={false}
        botonRegenrar={true}
        formConfigRegenerar={docForm}
        regenerar={(archivo: Archivo, values?: any) => {
          return onRegenerar(row, archivo, values);
        }}
        cincelFirma={archivoResp => onCincelFirma(row, archivoResp)}
      />
    </div>
  );
}

function renderBotonAsignar(row: DeportivaEventoAsignable, action) {
  if (!userHasPermission(ASIGNAR_EVENTO)) {
    return false;
  }

  const idBoton = "id-" + row.idProductoInstancia + "-asignar";
  let colorButton = "#008080";
  let tooltip: any = row.esClasePersonalizada
    ? "Asignar clase"
    : "Registrar participación";
  let habilitado = true;

  if (!row.isPagado) {
    habilitado = false;
    colorButton = "#600";
    tooltip = "La venta ha sido cancelada";
  } else if (row.caducado && !row.ignorarCaducidad) {
    habilitado = false;
    colorButton = "#600";
    tooltip = "El evento caducó.";
  } else if (!row.asignable) {
    habilitado = false;
    colorButton = "#600";
    tooltip = row.razonesNoAsignable.map(r => (
      <>
        {r}
        <br />
      </>
    ));
  }

  return renderBoton(
    row,
    idBoton,
    habilitado,
    action,
    tooltip,
    colorButton,
    faPlusCircle
  );
}

function renderBotonOmitirCaducidad(
  row: DeportivaEventoAsignable,
  action: (row: DeportivaEventoAsignable) => void
) {
  if (!userHasPermission(OMITIR_CADUCIDAD)) {
    return null;
  }

  const idBoton = "id-" + row.idProductoInstancia + "-caducidad";
  const yaIgnorado = row.ignorarCaducidad || false;
  const icon = yaIgnorado ? faCalendarTimes : faCalendarCheck;
  let habilitado = true;
  let colorButton = "#008080";
  let tooltip = yaIgnorado
    ? "Restaurar validación de caducidad"
    : "Omitir validación de caducidad";

  if (!row.isPagado) {
    habilitado = false;
    colorButton = "#600";
    tooltip = "La venta ha sido cancelada, no puede configurar la caducidad";
  }

  return renderBoton(
    row,
    idBoton,
    habilitado,
    action,
    tooltip,
    colorButton,
    icon
  );
}

function renderBoton(
  row: DeportivaEventoAsignable,
  id,
  habilitado,
  action,
  tooltip,
  color,
  icon
) {
  if (habilitado) {
    return (
      <span id={id} onClick={() => action(row)} className={s.btn}>
        <FontAwesomeIcon icon={icon} color={color} />
        <UncontrolledTooltip placement="left" target={id}>
          {tooltip}
        </UncontrolledTooltip>
      </span>
    );
  } else {
    return (
      <span id={id} className={s.info}>
        <FontAwesomeIcon icon={icon} color={color} />
        <UncontrolledTooltip placement="left" target={id}>
          {tooltip}
        </UncontrolledTooltip>
      </span>
    );
  }
}
